/**
 * API client. Holds the tokens, refreshes the access token when it expires,
 * and drops the session when the refresh itself is rejected.
 *
 * Tokens live in localStorage: there is no offline data to protect here
 * (unlike pebro Sales), and the refresh token rotates on every use.
 */

interface Session {
  accessToken: string
  refreshToken: string
}

const SESSION_KEY = 'hub.session'

function readSession(): Session | undefined {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    return raw ? (JSON.parse(raw) as Session) : undefined
  } catch {
    return undefined
  }
}

let session: Session | undefined = readSession()
let refreshing: Promise<boolean> | undefined

function storeSession(next: Session | undefined): void {
  session = next
  try {
    if (next) localStorage.setItem(SESSION_KEY, JSON.stringify(next))
    else localStorage.removeItem(SESSION_KEY)
  } catch {
    // Private mode: the session lasts as long as the tab.
  }
}

export function isLoggedIn(): boolean {
  return session !== undefined
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export class SessionExpiredError extends Error {
  constructor() {
    super('Sitzung abgelaufen')
    this.name = 'SessionExpiredError'
  }
}

async function errorMessage(response: Response): Promise<string> {
  const body = (await response.json().catch(() => ({}))) as { error?: string }
  return body.error ?? `Fehler ${response.status}`
}

/** One refresh at a time — parallel 401s wait for the same rotation. */
async function refresh(): Promise<boolean> {
  if (!session) return false
  refreshing ??= (async () => {
    const response = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: session?.refreshToken }),
    })
    // Only a definite "no" ends the session. A 5xx during a deploy must not
    // send the home-screen app back to the login.
    if (response.status === 401) {
      storeSession(undefined)
      return false
    }
    if (!response.ok) throw new ApiError(response.status, await errorMessage(response))
    storeSession((await response.json()) as Session)
    return true
  })().finally(() => {
    refreshing = undefined
  })
  return refreshing
}

export async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(session ? { Authorization: `Bearer ${session.accessToken}` } : {}),
      ...init.headers,
    },
  })
  if (response.status === 401 && retry) {
    if (await refresh()) return request<T>(path, init, false)
    throw new SessionExpiredError()
  }
  if (!response.ok) throw new ApiError(response.status, await errorMessage(response))
  return (await response.json()) as T
}

/** Sends a one-time code to the fixed login address. Returns the masked address. */
export async function requestCode(): Promise<{ sentTo: string; cooldown: boolean }> {
  const response = await fetch('/api/v1/auth/code', { method: 'POST' })
  if (!response.ok) throw new ApiError(response.status, await errorMessage(response))
  return (await response.json()) as { sentTo: string; cooldown: boolean }
}

export async function verifyCode(code: string): Promise<void> {
  const response = await fetch('/api/v1/auth/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, deviceName: navigator.userAgent.slice(0, 100) }),
  })
  if (!response.ok) throw new ApiError(response.status, await errorMessage(response))
  storeSession((await response.json()) as Session)
}

export async function logout(): Promise<void> {
  const token = session?.refreshToken
  storeSession(undefined)
  if (token) {
    await fetch('/api/v1/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: token }),
    }).catch(() => undefined)
  }
}

/** A protected file (thumbnail) as a blob; undefined when it is missing. */
export async function authorizedBlob(path: string, retry = true): Promise<Blob | undefined> {
  const response = await fetch(path, { headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {} })
  if (response.status === 401 && retry && (await refresh())) return authorizedBlob(path, false)
  return response.ok ? response.blob() : undefined
}
